import { ComponentType } from "@angular/cdk/portal";
import {
  Component,
  inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  Inject,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";

import { lastValueFrom } from "rxjs";

import { DeliveryOverlayService } from "@components/deliveries/pages/delivery-overlay/delivery-overlay.service";
import { DeliveryRiskAssessmentReportRelatedRecordsService } from "@components/deliveries/services";
import {
  AddEudrDdsDialogComponent,
  AddEudrDdsIdentifierDialogComponent,
  AddEudrDdsReferenceNumberDialogComponent,
  ConfirmDialogComponent,
  IItemQuantityDialogPayload,
  SlideOverlayDeliveryItemsComponent,
} from "@components/index";
import { BulkAddItemsService } from "@components/items/bulk-add-items/bulk-add-items.service";
import { LocationOverlayService } from "@components/locations/pages/location-overlay/location-overlay.service";
import {
  OverlayCertificateAttachmentsComponent,
  OverlayDocumentAttachmentsComponent,
} from "@components/shared";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { SlideOverlayContentComponent } from "@components/shared/overlay/slide-overlay-content/slide-overlay-content.component";
import { SlideOverlayMenuItemDirective } from "@components/shared/overlay/slide-overlay-menu-item/slide-overlay-menu-item.directive";
import { SlideOverlaySupplyChainClass } from "@components/shared/overlay/slide-overlay-supply-chain/slide-overlay-supply-chain.component";
import { RiskAssessmentReportRecord } from "@components/shared/risk-assessment-reports/models";
import { OverlayRiskAssessmentReportsComponent } from "@components/shared/risk-assessment-reports/ui";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { TextConstants } from "@shared/constants";
import {
  AddEudrDdsDialogResponseEnum,
  AttachmentTargetEnum,
  AttachmentTypeEnum,
  ConfirmDialogResponseEnum,
  CustomFieldsResourceTypeEnum,
  DeliveryStatusEnum,
  EntityTypeEnum,
  ExtensionProviderEnum,
  FeatureFlagEnum,
  LinkDirectionEnum,
  OverlayTabEnum,
  RecordStateEnum,
  RouteEnum,
} from "@shared/enums";
import {
  IConfig,
  IDelivery,
  IDeliveryDetails,
  IDeliveryPayload,
  IItem,
  IItemExtended,
  ILocationDetails,
  ILocationExtended,
  ILocationLink,
  IOrganisation,
  IProcessType,
  IProductExtended,
  IRecordResponse,
  IRecordState,
  ISelectOption,
  IShare,
} from "@shared/interfaces";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import {
  AttachmentsService,
  ConnectionsService,
  DeliveriesService,
  ExtensionsService,
  FeatureFlagService,
  ItemsService,
  LocalizationService,
  LocationsService,
  ProcessTypesService,
  RulesetsService,
  SharesService,
} from "@shared/services";
import { EudrDdsService } from "@shared/services/api/eudr-dds.service";
import { NavigationParams } from "@shared/services/router.service";
import { APP_CONFIG } from "@shared/tokens";
import { CommonUtils, CustomFieldsUtils, FormUtils, LocalizationUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-delivery-overlay",
  templateUrl: "./delivery-overlay.component.html",
  styleUrl: "./delivery-overlay.component.scss",
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DeliveryOverlayComponent
  extends SlideOverlaySupplyChainClass
  implements OnDestroy, OnInit
{
  @ViewChild("itemSection") itemSection: SlideOverlayDeliveryItemsComponent;

  @ViewChild("certificateSection") certificateSection: OverlayCertificateAttachmentsComponent;

  @ViewChild("slideOverlayContent") override slideOverlayContent: SlideOverlayContentComponent;

  @ViewChild("documentSection") documentSection: OverlayDocumentAttachmentsComponent;

  @ViewChild("riskAssessmentsView")
  readonly riskAssessmentsView: OverlayRiskAssessmentReportsComponent;

  public formGroup: UntypedFormGroup = new UntypedFormGroup({
    deliveryId: new UntypedFormControl(null, [CustomValidators.required]),
    purchaseOrder: new UntypedFormControl(null),
    from: new UntypedFormControl(null, [CustomValidators.required]),
    to: new UntypedFormControl(null, [CustomValidators.required]),
    status: new UntypedFormControl(null, [CustomValidators.required]),
    sent: new UntypedFormControl(null),
    delivered: new UntypedFormControl(null),
    agents: new UntypedFormControl(null),
  });

  public isLoadingItems = signal(false);

  public isLoadingShares = signal(false);

  public isLoadingMap = signal(true);

  public statusTagClass = signal<string>("");

  public hasDdsStatusUpdateFailed = signal<boolean>(null);

  public override element: IDelivery;

  public deliveryDetails: IDeliveryDetails;

  public override entityType = EntityTypeEnum.DELIVERIES;

  public fromLocationOptions: ISelectOption[] = [];

  public toLocationOptions: ISelectOption[] = [];

  public availableItems = signal<IItemExtended[]>([]);

  private allItems = signal<IItem[]>([]);

  private extendedItems = signal<IItemExtended[]>([]);

  public selectedItems = signal<IItemExtended[]>([]);

  public rulesetsRecords: IRecordResponse[];

  public readonly supplyChainHeightFn = CommonUtils.getOverlaySupplyChainHeight;

  public organisationsOptions: ISelectOption[];

  public allProcessTypes: IProcessType[] = [];

  public allOrganisations: IOrganisation[];

  public shares: IShare[] = [];

  public readonly attachmentTypeEnum = AttachmentTypeEnum;

  public readonly attachmentTargetEnum = AttachmentTargetEnum;

  public readonly recordStateEnum = RecordStateEnum;

  readonly riskAssessmentReportRelatedRecords = signal<{
    data: RiskAssessmentReportRecord["record"][];
    isLoading: boolean;
  }>({ data: undefined, isLoading: undefined });

  public override readonly attachmentTargetType = AttachmentTargetEnum.DELIVERY;

  readonly templateResourceType = RiskAssessmentTemplateResourceType.DELIVERY;

  public readonly isSharedUser = this.authenticationService.isSharedUser();

  public readonly isCrossOrgSharingEnabled = this.featureFlagService.isEnabled(
    FeatureFlagEnum.CROSS_ORGANISATION_SHARING,
  );

  public readonly isPurchaseOrdersEnabled = this.featureFlagService.isEnabled(
    FeatureFlagEnum.PURCHASE_ORDERS,
  );

  override menuItems = signal(
    new Map([
      [OverlayTabEnum.DETAILS, { title: TextConstants.DELIVERY_DETAILS, isEnabled: true }],
      [OverlayTabEnum.ITEMS, { title: TextConstants.ITEMS, isEnabled: false }],
      [OverlayTabEnum.CERTIFICATES, { title: TextConstants.CERTIFICATES, isEnabled: false }],
      [OverlayTabEnum.DOCUMENTS, { title: TextConstants.DOCUMENTS, isEnabled: false }],
      [OverlayTabEnum.SUPPLY_CHAIN, { title: TextConstants.SUPPLY_CHAIN, isEnabled: false }],
      [
        OverlayTabEnum.SHARES,
        { title: TextConstants.SHARES, isEnabled: false, isHidden: !this.isRegularUser },
      ],
      [
        OverlayTabEnum.RISK_ASSESSMENT_REPORTS,
        {
          title: TextConstants.RISK_ASSESSMENT_REPORTS,
          isEnabled: false,
          isHidden: !this.isRegularUser,
        },
      ],
      [
        OverlayTabEnum.COMMENTS,
        { title: TextConstants.COMMENTS, isEnabled: false, isHidden: !this.isRegularUser },
      ],
    ]),
  );

  public readonly statusOptions: ISelectOption[] = [
    {
      label: $localize`:@@delivery-overlay.status.delivered:Delivered`,
      value: DeliveryStatusEnum.DELIVERED,
    },
    {
      label: $localize`:@@delivery-overlay.status.pending:Pending`,
      value: DeliveryStatusEnum.PENDING,
    },
    {
      label: $localize`:@@delivery-overlay.status.inprogress:In progress`,
      value: DeliveryStatusEnum.IN_PROGRESS,
    },
  ];

  public allLocations: ILocationExtended[] = [];

  private lastFromLocationIdSelected: string;

  public isEudrExtensionEnabled = false;

  public ddsReference = signal<EudrDds.DdsReference>(null);

  public isDdsServiceUnavailable = false;

  public currentItemsAdvancedSearch = signal<any>(undefined);

  public readonly serviceUnavailableMsg = TextConstants.SERVICE_UNAVAILABLE;

  public readonly translations = {
    missingTypesTp: TextConstants.MISSING_DOC_TYPES_TOOLTIP,
    productSummary: TextConstants.PRODUCT_SUMMARY,
    eudrDds: TextConstants.EUDR_DDS,
    internalReferenceNumber: $localize`:@@shared.internalreferencenumber:Internal reference number`,
    ddsIdentifier: $localize`:@@delivery-overlay.dds-identifier:DDS identifier`,
    ddsIdentifierTooltip: $localize`:@@delivery-overlay.dds-identifier-tooltip:The unique identifier of the DDS on TRACES (not the Reference Number / Verification Number / Internal Reference Number). Is usually stated as the last part of the DDS URL in the browser (e.g. 6a6561d2-86e4-46fd-9814-6094ab3c64ff).`,
    referenceNumber: $localize`:@@eudr.referencenumber:Reference number`,
    verificationNumber: $localize`:@@delivery-overlay.verification-number:Verification number`,
  };

  private readonly locale: string = inject(LOCALE_ID);

  constructor(
    public deliveryOverlayService: DeliveryOverlayService,
    public deliveriesService: DeliveriesService,
    private locationsService: LocationsService,
    private itemsService: ItemsService,
    private connectionsService: ConnectionsService,
    private rulesetsService: RulesetsService,
    private processTypesService: ProcessTypesService,
    private sharesService: SharesService,
    private featureFlagService: FeatureFlagService,
    private extensionsService: ExtensionsService,
    private eudrDdsService: EudrDdsService,
    private locationOverlayService: LocationOverlayService,
    private bulkAddItemsService: BulkAddItemsService,
    private localizationService: LocalizationService,
    private attachmentsService: AttachmentsService,
    private riskAssessmentReportRelatedRecordsService: DeliveryRiskAssessmentReportRelatedRecordsService,
    @Inject(APP_CONFIG) private environment: IConfig,
  ) {
    super();

    CommonUtils.loadGoogleMapsScripts(
      this.environment.googleMapsApiKey,
      this.localizationService.getLanguage(),
    );
  }

  public override get isSubmitButtonDisabled(): boolean {
    if (!this.isEditing()) {
      return false;
    }

    return this.formGroup?.invalid || this.formGroup?.pending || !this.hasFormValuesChanged();
  }

  loadCounters(): void {
    if (this.recordId) {
      this.loadDocuments();
      this.loadCertificates();
      this.loadComments();

      if (this.isRegularUser) {
        this.deliveryOverlayService.loadReportsCounter(this.entityUri);
      }
    }
  }

  get allProducts(): IProductExtended[] {
    return this.deliveryOverlayService.allProducts;
  }

  setCountersToLoadingState() {
    this.deliveryOverlayService.setCountersToLoadingState();
    this.selectedItems.set([]);
  }

  setCountersEmptyState() {
    this.deliveryOverlayService.setCountersEmptyState();
  }

  loadDocuments(): void {
    this.deliveryOverlayService.loadDocumentCounter(this.element.id, this.attachmentTargetType);
  }

  loadCertificates(): void {
    this.deliveryOverlayService.loadCertificateCounter(this.element.id, this.attachmentTargetType);
  }

  loadComments(): void {
    this.deliveryOverlayService.loadCommentCounter(this.entityUri);
  }

  public async ngOnInit(): Promise<void> {
    this.overlay.showLoading();

    if (!this.isOnCorrectOverlay(RouteEnum.OVERLAY_DELIVERY)) {
      return;
    }

    await Promise.all([
      this.onReloadLocations(),
      this.setAllCustomFields(CustomFieldsResourceTypeEnum.DELIVERY),
      this.onReloadOrganisations(),
      this.onReloadProcessTypes(),
      (this.isEudrExtensionEnabled = (
        await this.extensionsService.getAllEnabled(this.activeOrganisationId)
      ).includes(ExtensionProviderEnum.EUDR)),
    ]);

    const recordId = this.recordId;

    if (recordId) {
      this.setCountersToLoadingState();

      await this.onReloadItems();
      const elementLoaded = await this.reloadElement(recordId);

      if (!elementLoaded) {
        return;
      }
      await Promise.all([this.setMenuItemFromURLParam(), this.onLoadDdsReport()]);

      this.loadCounters();
    } else {
      this.setupForm();
      this.overlay.dismissLoading();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.setCountersEmptyState();
  }

  public getLocationLink = (id: string): NavigationParams => {
    return this.routerService.getLocationLink(id, false) as NavigationParams;
  };

  private async onLoadDdsReport(): Promise<void> {
    try {
      const attachments = await this.attachmentsService.getAll(
        AttachmentTypeEnum.DDS_REFERENCE,
        `/organisations/${this.activeOrganisationId}/deliveries/${this.element.id}`,
      );

      const referenceUri = CommonUtils.getUriId(attachments[0].attachmentUri);

      const ddsReference = await this.eudrDdsService.get(referenceUri);

      this.ddsReference.set(ddsReference);
    } catch (error) {
      if (error.status === 503) {
        this.isDdsServiceUnavailable = true;
      }

      return;
    }

    if (!this.ddsReference()) {
      return;
    }

    if (this.hasEntitiesWriteAccess && this.ddsReference().status !== EudrDds.StatusEnum.PENDING) {
      this.updateEudrDdsReportStatus();
    } else {
      this.hasDdsStatusUpdateFailed.set(false);
    }
  }

  private async updateEudrDdsReportStatus(): Promise<void> {
    try {
      const ddsReference = await this.eudrDdsService.updateStatus(this.ddsReference().id);

      this.ddsReference.set(ddsReference);
      this.hasDdsStatusUpdateFailed.set(false);
    } catch {
      this.hasDdsStatusUpdateFailed.set(true);
    }
  }

  public onFromLocationSelected = async (newFromLocationOption: ISelectOption): Promise<void> => {
    if (!newFromLocationOption || typeof newFromLocationOption === "string") {
      this.fromLocationOptions = this.getLocationOptions(
        "FROM",
        this.formGroup.controls["to"].value,
      );
      this.toLocationOptions = this.getLocationOptions("TO");

      return;
    }

    await this.onChangeFromLocation(newFromLocationOption);
  };

  public onUpdateDeliveredQuantity = (data: IItemQuantityDialogPayload): void => {
    const selectedItem = this.selectedItems().find((i) => i.id === data.id);

    selectedItem.deliveredQuantity = data.deliveredQuantity;
    this.selectedItems.update((items) => [...items]);
    this.save(false, false);
  };

  public onAddNewItem = async (item: IItem): Promise<void> => {
    await this.onReloadItems();
    const itemExtended = this.extendedItems().find((i) => i.id === item.id);

    await this.onSetSelectedItems([
      ...this.selectedItems(),
      { ...itemExtended, deliveredQuantity: item.deliveredQuantity ?? item.initialQuantity },
    ]);
  };

  public onReloadItems = async (currentItemsAdvancedSearch?: any): Promise<void> => {
    this.isLoadingItems.set(true);
    if (currentItemsAdvancedSearch) {
      this.currentItemsAdvancedSearch.set(currentItemsAdvancedSearch);
    }

    const graphQLFilter = {
      ...this.currentItemsAdvancedSearch(),
      recordState: RecordStateEnum.ALL,
    };

    const extendedItems = await this.itemsService.getAllGraphQL(graphQLFilter, undefined, ["TAGS"]);

    this.extendedItems.set(extendedItems);

    const items: IItem[] = [];

    for (const extendedItem of extendedItems) {
      const item = CommonUtils.convertExtendedItemToItem(
        extendedItem,
        this.activeOrganisationId,
        this.isSharedUser,
      );

      items.push(item);
    }

    this.allItems.set(items);
    const availableItems = extendedItems.filter(
      (l) =>
        l.recordState === RecordStateEnum.ACTIVE &&
        !this.selectedItems().some((s) => s.id === l.id),
    );

    this.availableItems.set(
      availableItems.map((item) => {
        const product = item.product;
        const defaultCustomUnit = product?.defaultCustomUnit;

        if (defaultCustomUnit) {
          const customUnit = defaultCustomUnit;

          item.remainingQuantityFormatted = CommonUtils.formatQuantityWithDefaultUnit(
            this.locale,
            item.remainingQuantity,
            customUnit,
            item.product.unitOfMeasurement,
          );
          item.initialQuantityFormatted = CommonUtils.formatQuantityWithDefaultUnit(
            this.locale,
            item.initialQuantity,
            customUnit,
            item.product.unitOfMeasurement,
          );
        }

        return item;
      }),
    );

    this.isLoadingItems.set(false);
  };

  private async setDeliveryDetailsForMap(element: IDelivery): Promise<void> {
    this.isLoadingMap.set(true);

    let from: ILocationDetails;
    let to: ILocationDetails;
    const fromId = CommonUtils.getUriId(element.from);
    const toId = CommonUtils.getUriId(element.to);

    try {
      from = await this.locationsService.get(fromId);
    } catch {
      if (this.isRegularUser) {
        throw $localize`:@@error.delivery-overlay.errorfetchingfromlocation:Error fetching 'from' location`;
      }
    }
    try {
      to = await this.locationsService.get(toId);
    } catch {
      if (this.isRegularUser) {
        throw $localize`:@@error.delivery-overlay.errorfetchingtolocation:Error fetching 'to' location`;
      }
    }

    this.deliveryDetails = { ...element, from, to };
    this.isLoadingMap.set(false);
  }

  public override reloadElement = async (id: string): Promise<boolean> => {
    this.overlay.showLoading();

    try {
      this.element = await this.deliveriesService.get(id);
      this.setEditMode();
      this.setDeliveryDetailsForMap(this.element);

      this.setSelectedItems();
      this.updateAvailableItems();
      await this.onReloadOrganisations();

      this.setupForm();

      if (this.isEditing() && this.isRegularUser) {
        this.setRulesetRecords();
        this.reloadShares(true);
      }
      this.overlay.dismissLoading();

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    }
  };

  private updateAvailableItems(): void {
    const availableItems = this.extendedItems().filter(
      (l) =>
        l.recordState === RecordStateEnum.ACTIVE &&
        !this.selectedItems().some((s) => s.id === l.id),
    );

    this.availableItems.set(
      availableItems.map((item) => {
        const product = item.product;
        const defaultCustomUnit = product?.defaultCustomUnit;

        if (defaultCustomUnit) {
          const customUnit = defaultCustomUnit;

          item.remainingQuantityFormatted = CommonUtils.formatQuantityWithDefaultUnit(
            this.locale,
            item.remainingQuantity,
            customUnit,
            item.product.unitOfMeasurement,
          );
          item.initialQuantityFormatted = CommonUtils.formatQuantityWithDefaultUnit(
            this.locale,
            item.initialQuantity,
            customUnit,
            item.product.unitOfMeasurement,
          );
        }

        return item;
      }),
    );
  }

  public onSetSelectedItems = async (selectedItems: IItemExtended[]): Promise<void> => {
    this.isLoadingItems.set(true);

    this.selectedItems.set([...selectedItems]);

    const hasSaved = await this.save(false, false);

    if (hasSaved) {
      await this.onReloadItems();

      this.setSelectedItems();
    }
    this.isLoadingItems.set(false);
  };

  public override get itemIds(): string[] {
    return this.selectedItems().map((item) => item.id);
  }

  public override async save(
    isShowLoading: boolean = true,
    checkLocationLink: boolean = true,
  ): Promise<boolean> {
    if (await FormUtils.isInvalid(this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);
      this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

      return false;
    }

    if (isShowLoading) {
      this.overlay.showLoading();
    }

    const payload = this.getSavePayload();

    if (checkLocationLink) {
      const canContinue = await this.checkLocationLink(payload.from, payload.to);

      if (!canContinue) {
        return false;
      }
    }

    try {
      this.element = await this.deliveriesService.createOrUpdate(payload, this.element?.id);
      this.notificationService.showSuccess(
        this.isEditing()
          ? $localize`:@@delivery-overlay.deliverymodified:Delivery modified`
          : $localize`:@@delivery-overlay.deliverycreated:Delivery created`,
      );

      return true;
    } catch (error) {
      this.notificationService.showError(error);

      return false;
    } finally {
      this.overlay.dismissLoading();
    }
  }

  private async checkLocationLink(
    fromLocationUri: string,
    toLocationUri: string,
  ): Promise<boolean> {
    const fromLocationId = CommonUtils.getUriId(fromLocationUri);
    const toLocationId = CommonUtils.getUriId(toLocationUri);

    if (fromLocationId === toLocationId) {
      return true;
    }

    const fromLocation = this.allLocations.find((location) => location.id === fromLocationId);

    const locationLinks = (await this.locationOverlayService.loadLocationsLinks(
      fromLocationId,
      LinkDirectionEnum.FROM,
    )) as ILocationLink[];

    const hasLocationLink = locationLinks.some(
      (link) => CommonUtils.getUriId(link.to) === toLocationId,
    );

    if (hasLocationLink) {
      return true;
    }

    const toLocation = this.allLocations.find((location) => location.id === toLocationId);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: $localize`:@@action.delivery-overlay.addalocationlink:Add a location link`,
        contentText: $localize`:@@confirm.delivery-overlay.addlocationlink:There's no ${fromLocation.name}:from: → ${toLocation.name}:to: location link. Would you like to add it to your supply chain?`,
        confirmButtonText: $localize`:@@action.delivery-overlay.skipsave:Skip & save`,
        thirdActionButtonText: $localize`:@@action.delivery-overlay.addsave:Add & save`,
      },
    });

    const response = await lastValueFrom(dialogRef.afterClosed());

    if (response === ConfirmDialogResponseEnum.CONFIRM) {
      return true;
    }

    if (response === ConfirmDialogResponseEnum.THIRD_ACTION) {
      try {
        await this.locationOverlayService.createLocationLink(
          toLocationId,
          fromLocationId,
          LinkDirectionEnum.FROM,
        );

        this.notificationService.showSuccess($localize`:@@shared.linkcreated:Link created`);

        return true;
      } catch {
        this.notificationService.showError(
          $localize`:@@delivery-overlay.linkwasnotcreated:Link was not created`,
        );

        return true;
      }
    }

    return false;
  }

  override async afterSave(isSaveOnly: boolean): Promise<void> {
    if (isSaveOnly && !this.isEditing()) {
      await this.routerService.navigate(this.routerService.getDeliveryLink(this.element.id), {
        replaceUrl: true,
      });
    } else {
      this.reloadElement(this.element.id);
    }
    if (this.isEditing() && this.shouldUpdateTable) {
      this.loadDocuments();
    }
  }

  public confirmShareDialog(): void {
    this.deliveryOverlayService
      .share(this.activeOrganisationId, this.element.id)
      .subscribe(async (result) => {
        if (result?.hasSaved) {
          await this.reloadShares();
          this.changeDetectorRef.detectChanges();
          await this.changeMenuItem(OverlayTabEnum.SHARES);
        }
      });
  }

  confirmSendDialog() {
    this.deliveryOverlayService.send(this.activeOrganisationId, this.element.id);
  }

  public async setRulesetRecords(): Promise<void> {
    if (!this.isRegularUser) {
      this.rulesetsRecords = [];

      return;
    }
    try {
      this.rulesetsRecords = await this.rulesetsService.getInstantRulesetRecords(this.deliveryUri);
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  public override setupForm = (): void => {
    let fromValue: ISelectOption;
    let toValue: ISelectOption;
    let selectedAgents: string[] = [];
    let statusValue: ISelectOption = {
      label: $localize`:@@label.delivered:DELIVERED`,
      value: "DELIVERED",
    };

    if (this.isEditing()) {
      const fromId = CommonUtils.getUriId(this.element.from);
      const fromLocation = this.allLocations.find((l) => l.id === fromId);
      const selectedAgentsIds = this.element.agents.map((agent) => CommonUtils.getUriId(agent));

      statusValue = {
        label: LocalizationUtils.getStatus(this.element.status),
        value: this.element.status,
      };
      selectedAgents = (this.allOrganisations || [])
        .filter((org) => selectedAgentsIds.includes(org.id))
        .map((org) => org.id);
      if (fromLocation) {
        fromValue = { label: fromLocation.name, value: fromLocation.id };
        this.lastFromLocationIdSelected = fromLocation.id;
      }
      const toId = CommonUtils.getUriId(this.element.to);
      const toLocation = this.allLocations.find((l) => l.id === toId);

      if (toLocation) {
        toValue = { label: toLocation.name, value: toLocation.id };
      }
    }

    const entityExistsValidatorArgs = {
      searchPropertyName: "deliveryId",
      searchPropertyErrorDisplayName: "ID",
    };

    this.formGroup = new UntypedFormGroup(
      {
        deliveryId: new UntypedFormControl(
          this.element?.deliveryId ?? null,
          [CustomValidators.required],
          [
            CustomValidators.entityAlreadyExists(
              this.deliveriesService,
              this.element?.id ?? this.route.snapshot.params["id"] ?? null,
              entityExistsValidatorArgs,
            ),
          ],
        ),
        purchaseOrder: new UntypedFormControl(this.element?.purchaseOrderNumber),
        from: new UntypedFormControl(fromValue, [CustomValidators.required]),
        to: new UntypedFormControl(toValue, [CustomValidators.required]),
        status: new UntypedFormControl(statusValue, [CustomValidators.required]),
        sent: new UntypedFormControl(this.element?.sent ?? null),
        delivered: new UntypedFormControl(this.element?.delivered ?? null),
        agents: new UntypedFormControl(selectedAgents),
      },
      CustomValidators.dateRules([
        {
          name: "sent",
          rules: [
            {
              type: ">",
              target: "delivered",
              errorMessage: $localize`:@@delivery-overlay.error.laterthandelivered:Cannot be later than delivered date`,
            },
          ],
        },
        {
          name: "delivered",
          rules: [
            {
              type: "<",
              target: "sent",
              errorMessage: $localize`:@@delivery-overlay.error.beforesent:Cannot be before than sent date`,
            },
          ],
        },
      ]),
    );

    this.visibleCustomFields = CustomFieldsUtils.getVisible(
      this.allCustomFields,
      this.element?.customFields,
    );
    CustomFieldsUtils.addToFormGroup(
      this.formGroup,
      this.visibleCustomFields,
      this.element?.customFields,
    );

    this.subscriptions.add(
      this.formGroup.controls["from"].valueChanges.subscribe(async (from: any) => {
        if (typeof from === "string") {
          return;
        }
        await this.onFromLocationSelected(from);
      }),
    );
    this.subscriptions.add(
      this.formGroup.controls["to"].valueChanges.subscribe((to: any) => {
        if (typeof to === "string") {
          return;
        }
        this.fromLocationOptions = this.getLocationOptions("FROM", to);
      }),
    );

    this.fromLocationOptions = this.getLocationOptions("FROM");
    this.toLocationOptions = this.getLocationOptions("TO");
    this.initialFormValue = this.formGroup.getRawValue();
    this.hasFormValuesChanged.set(false);
    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        this.hasFormValuesChanged.set(
          !this.formGroup.pristine && this.hasInitialFormValueChanged(this.formGroup.getRawValue()),
        );
      }),
    );
  };

  public async onAddEudrDDS(): Promise<void> {
    const dialogRef = this.dialog.open<
      AddEudrDdsDialogComponent,
      null,
      AddEudrDdsDialogResponseEnum
    >(AddEudrDdsDialogComponent);

    const response = await lastValueFrom(dialogRef.afterClosed());

    switch (response) {
      case AddEudrDdsDialogResponseEnum.GENERATE_NEW:
        this.onClickGenerateEudrReport();
        break;
      case AddEudrDdsDialogResponseEnum.ADD_EXISTING_DDS_IDENTIFIER:
        this.onAddExistingDds<AddEudrDdsIdentifierDialogComponent>(
          AddEudrDdsIdentifierDialogComponent,
        );
        break;
      case AddEudrDdsDialogResponseEnum.ADD_EXISTING_REFERENCE_NUMBER:
        this.onAddExistingDds<AddEudrDdsReferenceNumberDialogComponent>(
          AddEudrDdsReferenceNumberDialogComponent,
        );
        break;
    }
  }

  public onClickGenerateEudrReport(): void {
    const url = `${RouteEnum.EUDR_REPORT}/${this.element.id}`;

    CommonUtils.openInNewTab(url);
  }

  public async onAddExistingDds<T>(dialogComponent: ComponentType<T>) {
    const dialogRef = this.dialog.open<
      T,
      { delivery: IDelivery },
      { hasSaved: boolean; ddsReference: EudrDds.DdsReference }
    >(dialogComponent, {
      data: {
        delivery: this.element,
      },
    });

    const { hasSaved, ddsReference } = await lastValueFrom(dialogRef.afterClosed());

    if (hasSaved) {
      await this.submitExistingDds(ddsReference);
    }
  }

  private async submitExistingDds(ddsReference: EudrDds.DdsReference): Promise<void> {
    try {
      await this.createDdsReferenceAttachment(ddsReference);

      this.ddsReference.set(ddsReference);
      await this.updateEudrDdsReportStatus();

      const url = `${RouteEnum.EUDR_DDS}/${this.ddsReference().id}`;

      CommonUtils.openInNewTab(url);
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  private async createDdsReferenceAttachment(ddsReference: EudrDds.DdsReference): Promise<void> {
    await this.attachmentsService.create({
      targetUri: `/organisations/${this.activeOrganisationId}/deliveries/${this.element.id}`,
      attachmentUri: `/organisations/${this.activeOrganisationId}/extensions/eudr/references/${ddsReference.id}`,
    });
  }

  public onClickGenerateReport(): void {
    const url = `${RouteEnum.DELIVERY_REPORT}/${this.element.id}`;

    CommonUtils.openInNewTab(url);
  }

  public async onShareDeleted(): Promise<void> {
    await this.reloadShares();

    if (!this.shares.length) {
      await this.slideOverlayContent.selectDefaultMenuItem();
    }
  }

  protected override recordName(): string {
    return this.element?.deliveryId;
  }

  protected override async archiveRecord(id: string, payload: IRecordState): Promise<void> {
    await this.deliveriesService.setRecordState(payload, id);
  }

  protected override async deleteRecord(id: string): Promise<void> {
    await this.deliveriesService.delete(id);
  }

  private onReloadProcessTypes = async (): Promise<void> => {
    await this.processTypesService
      .getAll()
      .then((response: IProcessType[]) => {
        this.allProcessTypes = response;
      })
      .catch((error) => {
        this.notificationService.showError(error);
      });
  };

  private onReloadOrganisations = async (): Promise<void> => {
    try {
      this.allOrganisations = await this.connectionsService.getAll();
      this.organisationsOptions = this.allOrganisations
        .filter((org) => org.recordState === RecordStateEnum.ACTIVE)
        .map((org) => ({ label: org.name, value: org.id }));
    } catch (error) {
      this.notificationService.showError(error);
    }
  };

  private onChangeFromLocation = async (newFromLocationOption: ISelectOption): Promise<void> => {
    this.toLocationOptions = this.getLocationOptions("TO", newFromLocationOption);
    const location = this.allLocations.find((c) => c.id === newFromLocationOption.value);

    if (location && location.id !== this.lastFromLocationIdSelected) {
      this.lastFromLocationIdSelected = newFromLocationOption.value.toString();
    }
  };

  private onReloadLocations = async (): Promise<void> => {
    await this.locationsService
      .getAllGraphQL()
      .then((response: ILocationExtended[]) => {
        this.allLocations = response;
      })
      .catch((error) => {
        this.notificationService.showError(error);
      });
  };

  private setSelectedItems = (): void => {
    this.selectedItems.set([]);
    const selectedItems: IItemExtended[] = [];

    for (const elementItem of this.element.items) {
      const itemId = CommonUtils.getUriId(elementItem.item);
      const item = this.extendedItems().find((i) => i.id === itemId);

      if (item) {
        selectedItems.push({ ...item, deliveredQuantity: elementItem.quantity });
      }
    }
    this.selectedItems.set([...selectedItems]);
  };

  async onMenuItemChanged(menuItem: SlideOverlayMenuItemDirective): Promise<void> {
    if (this.isRegularUser && menuItem.tab() === OverlayTabEnum.RISK_ASSESSMENT_REPORTS) {
      await this.setRiskAssessmentReportRelatedRecords();
    }
  }

  private async setRiskAssessmentReportRelatedRecords(): Promise<void> {
    if (!this.isRegularUser) {
      return;
    }

    this.riskAssessmentReportRelatedRecords.update((state) => ({ ...state, isLoading: true }));

    const data = await this.riskAssessmentReportRelatedRecordsService.get({
      id: this.element.id,
      itemsIds: this.itemIds,
    });

    this.riskAssessmentReportRelatedRecords.set({ data, isLoading: false });
  }

  private getSavePayload = (): IDeliveryPayload => {
    const fromValueId = this.formGroup.controls["from"].value.value;
    const toValueId = this.formGroup.controls["to"].value?.value;

    const agents: string[] = this.formGroup.controls["agents"]
      .getRawValue()
      ?.map(
        (agentId: string) => `/organisations/${this.activeOrganisationId}/connections/${agentId}`,
      );
    const payload: IDeliveryPayload = {
      id: this.element?.id ?? undefined,
      purchaseOrderNumber: this.formGroup.controls["purchaseOrder"].value,
      deliveryId: this.formGroup.controls["deliveryId"].value,
      from: `/organisations/${this.activeOrganisationId}/locations/${fromValueId}`,
      to: `/organisations/${this.activeOrganisationId}/locations/${toValueId}`,
      status: this.formGroup.controls["status"].value?.value,
      sent: FormUtils.getDateValueForPayload(this.formGroup.controls["sent"].value),
      delivered: FormUtils.getDateValueForPayload(this.formGroup.controls["delivered"].value),
      items: this.selectedItems().map((item) => ({
        item: `/organisations/${this.activeOrganisationId}/items/${item.id}`,
        quantity: +LocalizationUtils.getCleanNumber(item.deliveredQuantity.toString()),
      })),
      agents,
    };

    CustomFieldsUtils.addToPayload(
      payload,
      this.activeOrganisationId,
      this.formGroup,
      this.visibleCustomFields,
    );

    return payload;
  };

  private getLocationOptions = (type: string, avoidLocation?: ISelectOption): ISelectOption[] => {
    let filteredLocations: ILocationExtended[];

    if (type === "FROM") {
      const selectedFromId = this.element?.from ? CommonUtils.getUriId(this.element.from) : null;

      filteredLocations = this.allLocations.filter(
        (l) =>
          l.recordState === RecordStateEnum.ACTIVE || (selectedFromId && l.id === selectedFromId),
      );
    } else {
      const selectedToId = this.element?.to ? CommonUtils.getUriId(this.element.to) : null;

      filteredLocations = this.allLocations.filter(
        (l) => l.recordState === RecordStateEnum.ACTIVE || (selectedToId && l.id === selectedToId),
      );
    }

    if (!avoidLocation?.value) {
      return [...filteredLocations.map((l: ILocationExtended) => ({ label: l.name, value: l.id }))];
    }

    return [
      ...filteredLocations
        .filter((l: ILocationExtended) => l.id !== avoidLocation.value)
        .map((l: ILocationExtended) => ({ label: l.name, value: l.id })),
    ];
  };

  private get deliveryUri(): string {
    if (!this.element) {
      return null;
    }

    return `/organisations/${this.activeOrganisationId}/deliveries/${this.element.id}`;
  }

  private async reloadShares(isFirstLoad = false): Promise<void> {
    if (!this.recordId || !this.isRegularUser) {
      this.shares = [];
      this.deliveryOverlayService.linkedSharesCount.set(0);

      return;
    }

    this.isLoadingShares.set(true);
    try {
      this.shares = await this.sharesService.getAll(this.deliveryUri);

      this.deliveryOverlayService.linkedSharesCount.set(this.shares.length);

      this.isLoadingShares.set(false);

      if (
        isFirstLoad &&
        this.tabQueryParam &&
        CommonUtils.textToEnum(this.tabQueryParam) === OverlayTabEnum.SHARES &&
        this.deliveryOverlayService.linkedSharesCount()
      ) {
        this.changeDetectorRef.detectChanges();
        await this.setMenuItemFromURLParam();
      }
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  public traderTagPressed(tag: InputSelectOption): void {
    this.routerService.navigate(this.routerService.getOrganisationLink(tag.value as string));
  }

  public async onOpenBulkAddItemsSlideOver(): Promise<void> {
    await this.bulkAddItemsService.openSlideOver({ deliveryId: this.element.id });
  }
}
